import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { ImagePicker, GalleryPicker } from './ImageUploader';

export type FieldDef =
  | { name: string; label: string; type: 'text' | 'number'; required?: boolean; disabledOnEdit?: boolean; helper?: string }
  | { name: string; label: string; type: 'select'; options: { value: string; label: string }[]; required?: boolean }
  | { name: string; label: string; type: 'multiselect'; options: { value: string; label: string }[] }
  | { name: string; label: string; type: 'switch'; helper?: string }
  | { name: string; label: string; type: 'image' }
  | { name: string; label: string; type: 'gallery' };

interface Props {
  open: boolean;
  title: string;
  fields: FieldDef[];
  initial: Record<string, any>;
  isEdit: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (values: Record<string, any>) => void;
  onError?: (msg: string) => void;
}

const EditorDialog: React.FC<Props> = ({
  open,
  title,
  fields,
  initial,
  isEdit,
  saving,
  onClose,
  onSave,
  onError,
}) => {
  const [form, setForm] = useState<Record<string, any>>(initial);

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const set = (name: string, value: any) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  const handleSave = () => {
    for (const f of fields) {
      if ((f as any).required && !String(form[f.name] ?? '').trim()) {
        onError?.(`${f.label} is required`);
        return;
      }
    }
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {fields.map((f) => {
            const value = form[f.name];
            switch (f.type) {
              case 'text':
              case 'number':
                return (
                  <TextField
                    key={f.name}
                    label={f.label}
                    type={f.type}
                    size="small"
                    fullWidth
                    helperText={(f as any).helper}
                    value={value ?? ''}
                    disabled={isEdit && (f as any).disabledOnEdit}
                    onChange={(e) =>
                      set(f.name, f.type === 'number' ? Number(e.target.value) : e.target.value)
                    }
                  />
                );
              case 'select':
                return (
                  <TextField
                    key={f.name}
                    select
                    label={f.label}
                    size="small"
                    fullWidth
                    value={value ?? ''}
                    onChange={(e) => set(f.name, e.target.value)}
                  >
                    {f.options.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              case 'multiselect':
                return (
                  <Select
                    key={f.name}
                    multiple
                    size="small"
                    displayEmpty
                    value={Array.isArray(value) ? value : []}
                    onChange={(e) =>
                      set(f.name, typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
                    }
                    input={<OutlinedInput />}
                    renderValue={(selected) =>
                      (selected as string[]).length
                        ? (selected as string[]).join(', ')
                        : f.label
                    }
                  >
                    {f.options.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        <Checkbox checked={(Array.isArray(value) ? value : []).indexOf(o.value) > -1} />
                        <ListItemText primary={o.label} />
                      </MenuItem>
                    ))}
                  </Select>
                );
              case 'switch':
                return (
                  <FormControlLabel
                    key={f.name}
                    control={
                      <Switch
                        checked={value === undefined ? true : !!value}
                        onChange={(e) => set(f.name, e.target.checked)}
                      />
                    }
                    label={f.label}
                  />
                );
              case 'image':
                return (
                  <ImagePicker
                    key={f.name}
                    label={f.label}
                    value={value}
                    onChange={(url) => set(f.name, url)}
                    onError={onError}
                  />
                );
              case 'gallery':
                return (
                  <GalleryPicker
                    key={f.name}
                    label={f.label}
                    value={value}
                    onChange={(urls) => set(f.name, urls)}
                    onError={onError}
                  />
                );
              default:
                return null;
            }
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditorDialog;
